import React from "react";
import { Input, Tooltip, Checkbox } from "antd";
import { Editor } from "react-draft-wysiwyg";
import dayjs from "dayjs";

import { ChantInput, ChantSelect, ChantCheckbox } from "../../../../../../CommonComponents";
import ChantPopConfirm from "../../../../../../CommonComponents/ChantTooltip/ChantPopConfirm";
import FUpload from "../../../../../commons/formFields/FUpload";
import StripeSettings from "../StripeSettings";
import { ProcessingFeeTypes } from "./constants";
import logger from "../../../../../../utils/logger";

const processingFeeOptions = Object.keys(ProcessingFeeTypes).map((key) => ({
  value: key,
  label: ProcessingFeeTypes[key],
}));

const GeneralSetupStep = ({
  groupName,
  title,
  intro,
  benefits,
  groupImageList,
  appScreenshotListFiltered,
  groupTermsURL,
  thankYouNote,
  supportEmail,
  processingFee,
  requireMembershipPackage,
  joinSVG,
  stripeSetupConfirmation,
  showAddressVerification,
  addressVerification,
  addressVerificationTriggered,
  highlightPackageRequirement,
  backgroundColor,
  requireMembershipPackageRef,
  handleInputChange,
  handleEditorStateChange,
  handleImageChange,
  handleStripeSetupConfirmation,
  uploadAction,
  changeSelect,
  handleNext,
  setRequireMembershipPackage,
  setAddressVerification,
  setAddressVerificationTriggered,
}) => {
  const registrationUrl = `https://group.chant.fan/${groupName
    .replace(/ /g, "")
    .replace("#", "%23")
    .toLowerCase()}/join`;

  return (
    <>
      {/* Intro Note */}
      <div className="reg-intro-note">
        If &apos;Require Membership Package&apos; is checked, accounts will be
        locked after the &apos;Package Expiration Date&apos; for members without
        a current package. Provide a grace period to allow members time to renew.
      </div>

      {/* Registration URL Banner */}
      <div className="reg-url-banner">
        <div className="reg-url-banner-text">
          Once published, your registration URL will be:{" "}
          <a href={registrationUrl} target="_blank" rel="noopener noreferrer">
            {registrationUrl}
          </a>
        </div>
        {joinSVG && (
          <span dangerouslySetInnerHTML={{ __html: joinSVG }} />
        )}
      </div>

      {/* Basic Info Section */}
      <div className="reg-section">
        <h4 className="reg-section-title">Basic Information</h4>

        <div className="reg-field-grid">
          {/* Title */}
          <div className="reg-field">
            <ChantInput
              label={<span>Title <span className="required">*</span></span>}
              maxLength={40}
              value={title}
              onChange={(e) =>
                handleInputChange({
                  target: { name: "title", value: e.target.value },
                })
              }
              helpText={`${title.length}/40 characters`}
            />
          </div>

          {/* Brief Introduction - full width */}
          <div className="reg-field reg-field-full">
            <label className="reg-field-label">
              Brief Introduction <span className="required">*</span>
            </label>
            <Input.TextArea
              value={intro}
              name="intro"
              onChange={handleInputChange}
              rows={4}
              maxLength={250}
              style={{ resize: "vertical" }}
            />
            <span className="reg-field-hint">{intro.length}/250 characters</span>
          </div>

          {/* Member Benefits - full width */}
          <div className="reg-field reg-field-full">
            <label className="reg-field-label">Member Benefits</label>
            <div
              style={{
                border: "1px solid #d9d9d9",
                borderRadius: 8,
                padding: 8,
                minHeight: 250,
                overflow: "auto",
                backgroundColor: "#fff",
              }}
            >
              <Editor
                editorState={benefits}
                onEditorStateChange={handleEditorStateChange}
                toolbar={{
                  options: ["inline", "list", "link"],
                  inline: {
                    inDropdown: false,
                    options: ["bold", "italic", "underline"],
                  },
                  list: {
                    inDropdown: false,
                    options: ["unordered", "ordered"],
                  },
                  link: {
                    inDropdown: false,
                    defaultTargetOption: "_self",
                    options: ["link", "unlink"],
                    linkCallback: undefined,
                  },
                }}
                stripPastedStyles={true}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Images Section */}
      <div className="reg-section">
        <h4 className="reg-section-title">Images</h4>

        {/* Group Image */}
        <div className="reg-upload-row">
          <div className="reg-field">
            <label className="reg-field-label">
              Group Image <span className="required">*</span>
            </label>
            <div style={{ marginTop: 8 }}>
              <FUpload
                action={(file) => uploadAction(file, "groupImageFile")}
                fileList={groupImageList}
                onChange={({ fileList }) =>
                  handleImageChange(
                    fileList,
                    "groupImageFile",
                    "groupImageWideList",
                    "wide",
                  )
                }
                display="col"
                aspectRatio={21 / 9}
              />
            </div>
          </div>
          <div className="reg-upload-hint">
            Upload a landscape (21:9) image for the page header. Image should
            be 500KB minimum for best quality.
          </div>
        </div>

      </div>

      {/* Confirmation Page Section */}
      <div className="reg-section">
        <h4 className="reg-section-title">Confirmation Page</h4>

        <div className="reg-field-grid">
          {/* Thank You Message */}
          <div className="reg-field reg-field-full">
            <ChantInput
              label={<span>Thank You Message <span className="required">*</span></span>}
              value={thankYouNote}
              onChange={(e) =>
                handleInputChange({
                  target: { name: "thankYouNote", value: e.target.value },
                })
              }
              helpText={`Large header on confirmation page, e.g. 'Welcome to ${groupName}'`}
            />
          </div>

          {/* Support Email */}
          <div className="reg-field reg-field-full">
            <ChantInput
              label={<span>Support Email</span>}
              value={supportEmail}
              onChange={(e) =>
                handleInputChange({
                  target: { name: "supportEmail", value: e.target.value },
                })
              }
              helpText="Displayed on the confirmation page"
            />
          </div>

          {/* App Screenshot */}
          <div className="reg-field reg-field-full">
            <div className="reg-upload-row">
              <div className="reg-field">
                <label className="reg-field-label">
                  App Screenshot <span className="required">*</span>
                </label>
                <div style={{ marginTop: 8 }}>
                  <FUpload
                    action={(file) => uploadAction(file, "appScreenshotFile")}
                    fileList={appScreenshotListFiltered}
                    onChange={({ fileList }) =>
                      handleImageChange(
                        fileList,
                        "appScreenshotFile",
                        "appScreenshotList",
                        "wide",
                      )
                    }
                    display="col"
                  />
                </div>
              </div>
              <div className="reg-upload-hint">
                Populate the app then take a screenshot to appear on the
                confirmation page. Consider a matchday or when you have
                populated other content in the home feed.
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Additional Settings Section */}
      <div className="reg-section">
        <h4 className="reg-section-title">Additional Settings</h4>

        <div className="reg-field-grid">
          {/* Group Terms URL */}
          <div className="reg-field">
            <ChantInput
              label={<span>Group Terms (URL)</span>}
              value={groupTermsURL}
              onChange={(e) =>
                handleInputChange({
                  target: { name: "groupTermsURL", value: e.target.value },
                })
              }
              helpText="Members required to accept group and Chant terms"
            />
          </div>

          {/* Processing Fee - full width with hint */}
          <div className="reg-field reg-field-full">
            <div className="reg-field-with-hint">
              <div>
                <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4 }}>
                  <label className="reg-field-label" style={{ marginBottom: 0 }}>
                    Processing Fee
                  </label>
                  <Tooltip
                    placement="topLeft"
                    title={() => (
                      <div style={{ width: 400 }}>
                        <p style={{ fontWeight: "bold" }}>Fees</p>
                        <p style={{ marginBottom: 0 }}>
                          Stripe card processing fee: 2.9% + $0.30 (can vary)
                        </p>
                        <p>Chant fee: 3%</p>
                        <p style={{ fontWeight: "bold" }}>Member Pay</p>
                        <p>
                          Stripe collects the fee on the entire purchase amount.
                          So, if a member covers fees that is added to the total
                          charged ($10 item + $0.59 fee paid by member for $10.59)
                          stripe will collect a fee on the entire $10.59.
                        </p>
                        <p>
                          To account for this we add 3% + $.30 (.1% more) to the
                          original item price for Stripe so your net is close to
                          the item price - it may be off very slightly.
                        </p>
                        <p>
                          Stripe fees can vary based on member payment method but
                          are not known until after the transaction is complete
                          (foreign card, etc). Your net proceeds may not cover the
                          full item amount but should be close.
                        </p>
                        <p style={{ fontWeight: "bold" }}>
                          Member Pay (legal Limitations)
                        </p>
                        <p>
                          Not all states/countries allow the fee to be passed to
                          the member. Chant is not responsible for determining your
                          local laws. Please confirm this for your location.
                        </p>
                      </div>
                    )}
                  >
                    <span style={{ cursor: "help", color: "#888" }}>
                      <i className="fa fa-question-circle-o"></i>
                    </span>
                  </Tooltip>
                </div>
                <ChantSelect
                  value={processingFee || undefined}
                  onChange={(value) => changeSelect("processingFee", value)}
                  placeholder="--Select--"
                  options={processingFeeOptions}
                  helpText="Stripe (2.9% + $0.30) + Chant (3%). See '?' for additional details."
                />
              </div>
              <div className="reg-field-side-hint">
                This setting applies to registration, store, and donations when
                part of the membership package checkout flow. Settings for Store
                and Donations can be set separately when those features are used
                on their own.
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Require Membership Package */}
      <div
        ref={requireMembershipPackageRef}
        className={`reg-callout ${highlightPackageRequirement ? "package-requirement-highlight" : ""}`}
      >
        <div>
          <div className="reg-callout-label">
            Require membership package?
            <span className="reg-callout-badge">Important</span>
          </div>
          <ChantCheckbox
            checked={requireMembershipPackage}
            onChange={(e) => {
              setRequireMembershipPackage(e.target.checked);
              logger.log({ requireMembershipPackage: e.target.checked });
            }}
            label="Enable package requirement"
          />
        </div>
        <div className="reg-callout-info">
          <strong>Important:</strong> Selecting this field will use the package
          expiration date (set for each membership package you create) to lock
          members who do not purchase a package or renew next season. Reach out
          to <a href="mailto:support@chant.fan">support@chant.fan</a> if you
          have questions.
        </div>
      </div>

      {/* Stripe Settings (already a clean component) */}
      <StripeSettings
        stripeSetupConfirmation={stripeSetupConfirmation}
        handleStripeSetupConfirmation={handleStripeSetupConfirmation}
      />

      {/* Address Verification */}
      {showAddressVerification && (
        <div className="reg-section">
          <h4 className="reg-section-title">Address Verification</h4>
          <div className="reg-address-verify">
            <div>
              <label className="reg-field-label" style={{ marginBottom: 8, display: "block" }}>
                Verify subscriber addresses (subscribers only)
              </label>
              <div className="reg-address-verify-controls">
                <ChantPopConfirm
                  title="Warning: Option will force members with subscription renewals (who selected shipping) to update their address before entering the app."
                  onConfirm={() => {
                    setAddressVerification(dayjs().unix());
                    setAddressVerificationTriggered(true);
                  }}
                  okText="Yes"
                  disabled={addressVerificationTriggered}
                  cancelText="No"
                >
                  <Checkbox
                    checked={addressVerificationTriggered}
                    onChange={(e) => {
                      if (!e.target.checked) {
                        setAddressVerificationTriggered(false);
                      }
                    }}
                  />
                </ChantPopConfirm>
                {addressVerification && (
                  <span style={{ fontSize: 13, color: "#888" }}>
                    Last Triggered:{" "}
                    {dayjs.unix(addressVerification).format("Do MMM, YYYY")}
                  </span>
                )}
              </div>
            </div>
            <div className="reg-address-verify-hint">
              Use only when offering package subscriptions (auto-renewals) to
              verify subscriber addresses. Subscribers will see a popup
              requiring address verification before accessing Chant. Trigger
              after renewal date, before order fulfillment. Can retrigger each
              season.
            </div>
          </div>
        </div>
      )}

      {/* Navigation */}
      <div className="reg-step-nav">
        <button
          className="reg-btn reg-btn-next"
          type="button"
          style={{ backgroundColor: backgroundColor || "#1890ff" }}
          onClick={(e) => {
            e.preventDefault();
            handleNext("basicsStep", "formFieldStep");
          }}
        >
          Next
        </button>
      </div>
    </>
  );
};

export default GeneralSetupStep;
